import React, { useEffect, useState } from "react";
import { Subheading } from "../../../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../design/table";
import moment from "moment";
import ReportStatusBadge from "../../../../components/ReportStatusBadge";
import { ReportService } from "../../../../services/ReportService";
import { useAccount } from "../../../../contexts/AccountContext";
import { Report } from "@visitproof/types";
import LoadingView from "../../../../components/LoadingView";

interface Props {
  caseId: string;
}

const CaseDetailReportsList: React.FC<Props> = ({ caseId }) => {
  const { account } = useAccount();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      if (!account?.id) return;

      setLoading(true);
      try {
        const fetchedReports = await ReportService.getReportsByCaseId(caseId, account.id);
        setReports(fetchedReports);
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [caseId, account?.id]);

  if (loading) {
    return <LoadingView />;
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between w-full">
        <Subheading>Reports</Subheading>
      </div>
      {reports.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No reports have been created for this case yet.
          </p>
        </div>
      )}
      {reports.length > 0 && (
        <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Created</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow
                key={report.id}
                title={`Report #${report.id}`}
                href={`/report/${report.id}`}
              >
                <TableCell>
                  {report.createdAt
                    ? moment(report.createdAt.toDate()).format("lll")
                    : "Unknown"}
                </TableCell>
                <TableCell>
                  <ReportStatusBadge status={report.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default CaseDetailReportsList;
